/*
 * ====================
 * SteppedDrawer
 * ====================
 * A drawer component that allows for multiple screens
 * Built on top of @/components/ui/drawer
 * See https://www.radix-ui.com/primitives/docs/components/dialog
 * API is the same as Drawer, but with the addition of a Screen component
 * and a ScreenContainer component.
 *
 * Screen:
 * A component that represents a screen in the drawer,
 * with a name prop that is used to identify the screen.
 *
 * ScreenContainer:
 * A component that wraps the Screen components and provides a context
 * to manage the current screen.
 *
 * Usage:
 * Wrap the Screen components in a ScreenContainer component and use the
 * setScreen function to switch between screens.
 *
 * <SteppedDrawer.Root>
 *   <SteppedDrawer.Trigger>Open Drawer</SteppedDrawer.Trigger>
 *   <SteppedDrawer.Content>
 *     <SteppedDrawer.ScreenContainer>
 *       {({ currentScreen, setScreen }) => (
 *         <>
 *           <SteppedDrawer.Screen screenName="screen1">
 *             <h1>Screen 1</h1>
 *             <button onClick={() => setScreen('screen2')}>Next</button>
 *           </SteppedDrawer.Screen>
 *           <SteppedDrawer.Screen screenName="screen2">
 *             <h1>Screen 2</h1>
 *             <button onClick={() => setScreen('screen1')}>Back</button>
 *           </SteppedDrawer.Screen>
 *         </>
 *       )}
 *     </SteppedDrawer.ScreenContainer>
 *   </SteppedDrawer.Content>
 * </SteppedDrawer.Root>
 *
 * Built by: @sambeevors
 */

import { Slot } from 'radix-ui';
import type React from 'react';
import { useEffect } from 'react';
import { ScreenProvider, useScreenContext } from '@/components/SteppedContent/context/ScreenContext';
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerOverlay, DrawerPortal, DrawerTitle, DrawerTrigger } from '@music-vine/cadence/ui';
export interface DrawerScreenProps {
  asChild?: boolean;
  children: React.ReactNode;
  className?: string;
  screenName: string;
}
const DrawerScreen = ({
  screenName,
  children,
  asChild = false,
  className = ''
}: DrawerScreenProps) => {
  const {
    currentScreen,
    registerScreen
  } = useScreenContext();
  useEffect(() => {
    registerScreen(screenName);
  }, [screenName, registerScreen]);
  const isVisible = currentScreen === screenName;

  // If asChild is true, render the Slot component
  // This is used to render the screen into it's immediate child
  const Screen = asChild ? Slot.Root : 'div';
  if (!isVisible) {
    return null;
  }
  return <Screen className={className} data-drawer-screen={screenName} data-state={isVisible ? 'open' : 'closed'} data-sentry-element="Screen" data-sentry-component="DrawerScreen" data-sentry-source-file="index.tsx">
      {children}
    </Screen>;
};
export interface DrawerScreenContainerProps {
  children: React.ReactNode;
  initial?: string;
}
const DrawerScreenContainer = ({
  children,
  initial
}: DrawerScreenContainerProps) => <ScreenProvider initial={initial} data-sentry-element="ScreenProvider" data-sentry-component="DrawerScreenContainer" data-sentry-source-file="index.tsx">{children}</ScreenProvider>;

/*
 * Export the SteppedDrawer components along with the Drawer components.
 * This allows for the same API as Drawer, but with the addition of
 * Screen and ScreenContainer components.
 */
const SteppedDrawer = {
  Root: Drawer,
  Trigger: DrawerTrigger,
  Content: DrawerContent,
  Portal: DrawerPortal,
  Overlay: DrawerOverlay,
  Close: DrawerClose,
  Header: DrawerHeader,
  Footer: DrawerFooter,
  Title: DrawerTitle,
  Description: DrawerDescription,
  Screen: DrawerScreen,
  ScreenContainer: DrawerScreenContainer
};
export default SteppedDrawer;